import { supabase } from './supabase';

function html(body) {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, sans-serif; background: #f9fafb; margin: 0; padding: 32px 16px; }
    .card { background: #fff; border-radius: 12px; padding: 32px; max-width: 520px; margin: 0 auto; border: 1px solid #e5e7eb; }
    .logo { font-size: 22px; font-weight: 900; color: #111; margin-bottom: 4px; }
    .logo span { color: #16a34a; }
    .divider { height: 1px; background: #e5e7eb; margin: 20px 0; }
    .row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; }
    .label { color: #6b7280; }
    .value { font-weight: 600; color: #111; }
    .amount { font-size: 28px; font-weight: 900; color: #16a34a; }
    .footer { font-size: 12px; color: #9ca3af; text-align: center; margin-top: 24px; }
    p { font-size: 14px; line-height: 1.6; color: #374151; margin: 12px 0; }
    .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; }
    .badge-green { background: #dcfce7; color: #166534; }
    .badge-yellow { background: #fef9c3; color: #854d0e; }
    .badge-red { background: #fee2e2; color: #991b1b; }
  </style>
</head>
<body>
  <div class="card">
    <div class="logo">Rent<span>Lord</span></div>
    <div style="font-size:12px;color:#9ca3af;margin-bottom:20px">Property Management Platform</div>
    <div class="divider"/>
    ${body}
    <div class="footer">© 2026 RentLord · DFW, Texas · rentlordapp.com</div>
  </div>
</body>
</html>`;
}

function fmt$(n) {
  return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d + (d.length === 10 ? 'T12:00:00' : '')).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}

// ── Core send ─────────────────────────────────────────────────────────────────
async function sendEmail({ to, subject, htmlBody, text }) {
  return supabase.functions.invoke('send-email', {
    body: { to, subject, html: htmlBody, text },
  });
}

// ── Template: Payment Receipt ─────────────────────────────────────────────────
export async function sendPaymentReceipt({ tenantEmail, tenantName, payment }) {
  const subject = `Rent Receipt – ${payment.mth_name || fmtDate(payment.payment_date || payment.date)}`;
  const htmlBody = html(`
    <p>Hi ${tenantName},</p>
    <p>Your rent payment has been received and processed. Here are the details:</p>
    <div class="row"><span class="label">Receipt #</span><span class="value" style="font-family:monospace">${payment.reference_no || payment.ref || '—'}</span></div>
    <div class="row"><span class="label">Period</span><span class="value">${payment.mth_name || (payment.payment_date || '').slice(0, 7)}</span></div>
    <div class="row"><span class="label">Payment Date</span><span class="value">${fmtDate(payment.payment_date || payment.date)}</span></div>
    <div class="row"><span class="label">Method</span><span class="value">${payment.method || payment.mth || '—'}</span></div>
    <div class="divider"/>
    <div style="text-align:center;padding:16px 0">
      <div style="font-size:12px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px">Amount Paid</div>
      <div class="amount">${fmt$(payment.amount || payment.amt)}</div>
      <span class="badge badge-green" style="margin-top:8px">PAID</span>
    </div>
    <div class="divider"/>
    <p style="font-size:12px;color:#9ca3af">Keep this email as your official proof of payment.</p>
  `);
  return sendEmail({ to: tenantEmail, subject, htmlBody });
}

// ── Template: Maintenance Update ──────────────────────────────────────────────
export async function sendMaintenanceUpdate({ tenantEmail, tenantName, request, newStatus }) {
  const statusLabels = { open: 'Submitted', in_progress: 'In Progress', resolved: 'Resolved' };
  const statusLabel  = statusLabels[newStatus] || newStatus;
  const subject      = `Maintenance Update – ${request.title}`;
  const htmlBody = html(`
    <p>Hi ${tenantName},</p>
    <p>There's an update on your maintenance request:</p>
    <div class="row"><span class="label">Request</span><span class="value">${request.title}</span></div>
    <div class="row"><span class="label">Category</span><span class="value">${request.category || request.cat || '—'}</span></div>
    <div class="row"><span class="label">Priority</span><span class="value" style="text-transform:capitalize">${request.priority || request.pri || '—'}</span></div>
    <div class="row"><span class="label">New Status</span>
      <span class="badge ${newStatus === 'resolved' ? 'badge-green' : newStatus === 'in_progress' ? 'badge-yellow' : ''}">${statusLabel}</span>
    </div>
    ${newStatus === 'resolved' ? '<p>Your request has been resolved. Please let us know if you have any further concerns.</p>' : '<p>We are actively working on your request. We\'ll notify you when there are further updates.</p>'}
  `);
  return sendEmail({ to: tenantEmail, subject, htmlBody });
}

// ── Template: Late Rent Notice ────────────────────────────────────────────────
export async function sendLateNotice({ tenantEmail, tenantName, balanceDue, graceDays = 5 }) {
  const subject  = 'Rent Payment Reminder';
  const htmlBody = html(`
    <p>Hi ${tenantName},</p>
    <p>This is a friendly reminder that your rent payment is past due.</p>
    <div style="background:#fef2f2;border-radius:8px;padding:16px;text-align:center;margin:20px 0">
      <div style="font-size:12px;color:#991b1b;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px">Balance Due</div>
      <div class="amount" style="color:#dc2626">${fmt$(balanceDue)}</div>
    </div>
    <p>Please submit your payment as soon as possible to avoid late fees. If you have already paid, please disregard this notice.</p>
    <p>If you have questions or need to discuss a payment arrangement, please reach out through the tenant portal or reply to this email.</p>
  `);
  return sendEmail({ to: tenantEmail, subject, htmlBody });
}

// ── Template: Lease Renewal Offer ─────────────────────────────────────────────
export async function sendLeaseRenewalOffer({ tenantEmail, tenantName, lease }) {
  const subject  = 'Your Lease Renewal – Action Required';
  const expiry   = fmtDate(lease.end_date || lease.e);
  const htmlBody = html(`
    <p>Hi ${tenantName},</p>
    <p>Your lease is expiring soon and we'd love to have you stay. Here are the details:</p>
    <div class="row"><span class="label">Lease Expires</span><span class="value">${expiry}</span></div>
    <div class="row"><span class="label">Current Rent</span><span class="value">${fmt$(lease.monthly_rent || lease.rent)}</span></div>
    <div class="row"><span class="label">Unit</span><span class="value">${lease.unit || lease.u || '—'}</span></div>
    <div class="divider"/>
    <p>Please log in to your tenant portal to accept the renewal or let us know your plans. We need your decision at least 30 days before your lease ends.</p>
  `);
  return sendEmail({ to: tenantEmail, subject, htmlBody });
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useEmail() {
  return {
    sendEmail,
    sendPaymentReceipt,
    sendMaintenanceUpdate,
    sendLateNotice,
    sendLeaseRenewalOffer,
  };
}
